import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../context/AuthContext';

/**
 * ProfileCard — set the name you'd like to be called by, and optionally pronouns.
 *
 * Used by both students and instructors, so it lives in components/shared and
 * carries its own styling rather than importing a tab's CSS module.
 *
 * Pronouns are optional, blank by default, and shown only to the instructor
 * running the queue — never to other students. The card says so, because a
 * student deciding whether to fill this in deserves to know who will see it.
 */
export default function ProfileCard({ onSaved, compact = false }) {
  const { apiFetch, updateUser } = useAuth();

  const [profile, setProfile] = useState(null);
  const [name,     setName]    = useState('');
  const [pronouns, setPronouns] = useState('');
  const [busy,  setBusy]  = useState(false);
  const [done,  setDone]  = useState('');
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    try {
      const res = await apiFetch('/api/settings/profile');
      if (res.ok) {
        const d = await res.json();
        setProfile(d);
        setName(d.display_name || '');
        setPronouns(d.pronouns || '');
      } else {
        setError(`Could not load your profile (HTTP ${res.status}).`);
      }
    } catch (err) { setError(err.message); }
  }, [apiFetch]);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setBusy(true); setError(''); setDone('');
    try {
      const res = await apiFetch('/api/settings/profile', {
        method: 'PUT',
        body: JSON.stringify({
          display_name: name.trim(),
          pronouns: pronouns.trim() || null,
        }),
      });
      const d = await res.json().catch(() => ({}));
      if (res.ok) {
        setProfile(p => ({ ...p, display_name: d.display_name, pronouns: d.pronouns }));
        // Update the cached user so the header reflects the new name straight
        // away rather than after the next sign-in.
        updateUser?.({ display_name: d.display_name });
        setDone('Saved.');
        setTimeout(() => setDone(''), 4000);
        onSaved?.(d);
      } else {
        setError(typeof d.detail === 'string' ? d.detail : 'Could not save your profile.');
      }
    } catch (err) { setError(err.message); }
    finally { setBusy(false); }
  };

  if (!profile) {
    return error
      ? <div style={errBox}>{error}</div>
      : <p style={{ fontSize: 13, color: 'var(--txt-faint)' }}>Loading…</p>;
  }

  const dirty =
    name.trim() !== (profile.display_name || '') ||
    pronouns.trim() !== (profile.pronouns || '');

  return (
    <div style={compact ? compactWrap : {}}>
      {!compact && (
        <h3 style={titleStyle}>Your Profile</h3>
      )}

      {/* Announced, not just shown — a save that only changes pixels is
          invisible to anyone not watching them. */}
      {done  && <div style={okBox} role="status"><span aria-hidden="true">✓ </span>{done}</div>}
      {error && <div style={errBox} role="alert"><span aria-hidden="true">⚠ </span>{error}</div>}

      <div style={fieldWrap}>
        <label style={labelStyle} htmlFor="profile-display-name">Display name</label>
        <input
          id="profile-display-name"
          aria-describedby="profile-display-name-hint"
          value={name}
          onChange={e => setName(e.target.value)}
          maxLength={100}
          placeholder="What you'd like to be called"
          style={inputStyle}
        />
        <div style={hintStyle} id="profile-display-name-hint">
          This is the name your instructor sees in the queue and uses when
          messaging you.
        </div>
      </div>

      <div style={fieldWrap}>
        <label style={labelStyle} htmlFor="profile-pronouns">Pronouns (optional)</label>
        <input
          id="profile-pronouns"
          aria-describedby="profile-pronouns-hint"
          value={pronouns}
          onChange={e => setPronouns(e.target.value)}
          maxLength={40}
          placeholder="e.g. she/her, he/him, they/them, ask me"
          style={inputStyle}
        />
        <div style={hintStyle} id="profile-pronouns-hint">
          Shown to your instructor on the queue, so they know how to address you.
          Not shown to other students. Leave it blank if you'd rather not say —
          you can add or clear it at any time.
        </div>
      </div>

      <div style={fieldWrap}>
        <label style={labelStyle} htmlFor="profile-email">Email</label>
        <input id="profile-email" value={profile.email} disabled style={{ ...inputStyle, ...disabledStyle }} />
        <div style={hintStyle}>
          Your email and legal name ({profile.legal_name}) can't be changed here —
          they're how your instructor matches you to their roster.
        </div>
      </div>

      <button
        className="btn-primary"
        onClick={save}
        disabled={busy || !dirty || !name.trim()}
        style={{ marginTop: 4 }}
      >
        {busy ? 'Saving…' : dirty ? 'Save Profile' : 'Saved'}
      </button>
    </div>
  );
}

const compactWrap = {
  background: 'var(--bg-panel)',
  border: '1px solid var(--border)',
  borderRadius: 'var(--radius-lg, 12px)',
  padding: '18px 20px',
};

const titleStyle = {
  fontFamily: 'var(--font-display)',
  fontSize: 14,
  fontWeight: 700,
  color: 'var(--txt)',
  marginBottom: 14,
};

const fieldWrap = {
  display: 'flex',
  flexDirection: 'column',
  gap: 5,
  marginBottom: 16,
};

const labelStyle = {
  fontSize: 11,
  fontWeight: 600,
  color: 'var(--txt-dim)',
  textTransform: 'uppercase',
  letterSpacing: '0.06em',
};

const inputStyle = {
  background: 'var(--bg)',
  border: '1px solid var(--border)',
  borderRadius: 6,
  color: 'var(--txt)',
  fontSize: 13,
  padding: '8px 10px',
  fontFamily: 'inherit',
  width: '100%',
  boxSizing: 'border-box',
};

const disabledStyle = {
  opacity: 0.6,
  cursor: 'not-allowed',
};

const hintStyle = {
  fontSize: 11,
  color: 'var(--txt-faint)',
  lineHeight: 1.5,
};

const okBox = {
  background: 'var(--green-bg)',
  border: '1px solid var(--green)',
  borderRadius: 6,
  color: 'var(--green)',
  fontSize: 13,
  padding: '8px 10px',
  marginBottom: 12,
};

const errBox = {
  background: 'var(--red-bg)',
  border: '1px solid var(--red)',
  borderRadius: 6,
  color: 'var(--red)',
  fontSize: 13,
  padding: '8px 10px',
  marginBottom: 12,
};
